import React, { FormEvent, useEffect, useMemo, useState } from "react";
import {
  deleteOwnAccount,
  requestPasswordReset,
  resetPassword,
  session,
  signInWithEmail,
  signOut,
  signUpWithEmail,
  type AppAuthStatus,
  type AppUser,
} from "../auth";

type Mode = "sign-in" | "create" | "forgot" | "reset";

export default function AuthGate({ children }: { children: React.ReactNode }) {
  const resetToken = useMemo(() => {
    if (typeof window === "undefined") return null;
    return new URLSearchParams(window.location.search).get("token");
  }, []);

  const [user, setUser] = useState<AppUser | null>(null);
  const [status, setStatus] = useState<AppAuthStatus>("signed_out");
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<Mode>(resetToken ? "reset" : "sign-in");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function refresh() {
    const next = await session.status();
    setUser(next.user);
    setStatus(next.status);
    setLoading(false);
  }

  useEffect(() => {
    void refresh();
  }, []);

  function chooseMode(nextMode: Mode) {
    setMode(nextMode);
    setError(null);
    setNotice(null);
    setPassword("");
    setConfirmPassword("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setWorking(true);
    setError(null);
    setNotice(null);

    try {
      if (mode === "create") {
        await signUpWithEmail(name.trim(), email.trim(), password);
        setPassword("");
        setMode("sign-in");
        setNotice(
          "Account created. Check your email and verify it before signing in.",
        );
        return;
      }

      if (mode === "forgot") {
        await requestPasswordReset(email.trim());
        setNotice(
          "If that email belongs to a CatchCompass account, a password-reset link is on its way.",
        );
        return;
      }

      if (mode === "reset") {
        if (!resetToken) throw new Error("This password-reset link is missing its token.");
        if (password !== confirmPassword)
          throw new Error("The two passwords do not match.");
        await resetPassword(password, resetToken);
        if (typeof window !== "undefined")
          window.history.replaceState({}, "", "/login");
        setPassword("");
        setConfirmPassword("");
        setMode("sign-in");
        setNotice("Password updated. You can sign in with your new password.");
        return;
      }

      await signInWithEmail(email.trim(), password);
      if (typeof window !== "undefined")
        window.history.replaceState({}, "", "/app");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setWorking(false);
    }
  }

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-6 text-primary">
        <div className="text-center">
          <div className="mx-auto mb-4 size-10 animate-pulse rounded-full border-2 border-accent" />
          <p className="text-sm text-secondary">Opening CatchCompass…</p>
        </div>
      </div>
    );
  }

  if (user && status === "ready") {
    if (
      typeof window !== "undefined" &&
      window.location.pathname === "/login"
    ) {
      window.history.replaceState({}, "", "/app");
    }
    return <>{children}</>;
  }

  if (status === "verification_required") {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold text-primary">One secure step left</h1>
        <p className="mt-3 text-sm leading-6 text-secondary">
          This email matches an existing CatchCompass angler. We will not attach
          the old lakes, sessions or catches until ownership of the email has
          been verified.
        </p>
        <div className="mt-5 rounded-lg border border-border bg-inset p-4 text-sm text-secondary">
          Your fishing history has not moved or changed. The new key simply has
          not been linked to it yet.
        </div>
        <button
          type="button"
          className="mt-6 w-full rounded-lg border border-border px-4 py-3 text-sm font-semibold text-primary"
          onClick={signOut}
        >
          Sign out
        </button>
        <LockedAccountDelete />
      </AuthShell>
    );
  }

  if (status === "pending_approval") {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold text-primary">Awaiting approval</h1>
        <p className="mt-3 text-sm leading-6 text-secondary">
          Your CatchCompass account has been created and verified. Access will
          open once an administrator approves it.
        </p>
        <div className="mt-5 rounded-lg border border-border bg-inset p-4 text-sm text-secondary">
          Your account is safe. You do not need to create another login.
        </div>
        <button
          type="button"
          className="mt-6 w-full rounded-lg border border-border px-4 py-3 text-sm font-semibold text-primary"
          onClick={signOut}
        >
          Sign out
        </button>
        <LockedAccountDelete />
      </AuthShell>
    );
  }

  if (status === "declined") {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold text-primary">Access not authorised</h1>
        <p className="mt-3 text-sm leading-6 text-secondary">
          This CatchCompass account request has been declined by an administrator.
        </p>
        <div className="mt-5 rounded-lg border border-border bg-inset p-4 text-sm text-secondary">
          No fishing data has been deleted. An administrator can approve the
          account later if access should be restored.
        </div>
        <button
          type="button"
          className="mt-6 w-full rounded-lg border border-border px-4 py-3 text-sm font-semibold text-primary"
          onClick={signOut}
        >
          Sign out
        </button>
        <LockedAccountDelete />
      </AuthShell>
    );
  }

  if (status === "suspended") {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold text-primary">Account suspended</h1>
        <p className="mt-3 text-sm leading-6 text-secondary">
          Access to this CatchCompass account has been temporarily suspended.
        </p>
        <div className="mt-5 rounded-lg border border-border bg-inset p-4 text-sm text-secondary">
          Your fishing data remains stored. An administrator can reinstate
          access without creating a new account.
        </div>
        <button
          type="button"
          className="mt-6 w-full rounded-lg border border-border px-4 py-3 text-sm font-semibold text-primary"
          onClick={signOut}
        >
          Sign out
        </button>
        <LockedAccountDelete />
      </AuthShell>
    );
  }

  if (status === "link_conflict") {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold text-primary">Identity needs review</h1>
        <p className="mt-3 text-sm leading-6 text-secondary">
          CatchCompass found more than one possible identity link, so it stopped
          instead of guessing. No fishing data has been changed.
        </p>
        <button
          type="button"
          className="mt-6 w-full rounded-lg border border-border px-4 py-3 text-sm font-semibold text-primary"
          onClick={signOut}
        >
          Sign out
        </button>
        <LockedAccountDelete />
      </AuthShell>
    );
  }

  const isReset = mode === "reset";
  const isForgot = mode === "forgot";

  return (
    <AuthShell>
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
          A point in the right direction
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-primary">
          CatchCompass
        </h1>
        <p className="mt-2 text-sm text-secondary">
          {mode === "create"
            ? "Create your CatchCompass account."
            : isForgot
              ? "We’ll send a secure password-reset link."
              : isReset
                ? "Choose a new CatchCompass password."
                : "Sign in to your fishing log."}
        </p>
      </div>

      {!isForgot && !isReset ? (
        <div className="mb-5 grid grid-cols-2 rounded-lg bg-inset p-1">
          <button
            type="button"
            className={`rounded-md px-3 py-2 text-sm font-semibold ${
              mode === "sign-in"
                ? "bg-raised text-primary shadow-sm"
                : "text-secondary"
            }`}
            onClick={() => chooseMode("sign-in")}
          >
            Sign in
          </button>
          <button
            type="button"
            className={`rounded-md px-3 py-2 text-sm font-semibold ${
              mode === "create"
                ? "bg-raised text-primary shadow-sm"
                : "text-secondary"
            }`}
            onClick={() => chooseMode("create")}
          >
            Create account
          </button>
        </div>
      ) : null}

      <form className="space-y-4" onSubmit={submit}>
        {mode === "create" ? (
          <label className="block text-sm font-medium text-primary">
            Name
            <input
              className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-primary outline-none focus:border-accent"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              required
            />
          </label>
        ) : null}

        {!isReset ? (
          <label className="block text-sm font-medium text-primary">
            Email
            <input
              className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-primary outline-none focus:border-accent"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
        ) : null}

        {!isForgot ? (
          <label className="block text-sm font-medium text-primary">
            {isReset ? "New password" : "Password"}
            <input
              className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-primary outline-none focus:border-accent"
              type="password"
              minLength={8}
              maxLength={128}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={
                mode === "create" || isReset ? "new-password" : "current-password"
              }
              required
            />
          </label>
        ) : null}

        {isReset ? (
          <label className="block text-sm font-medium text-primary">
            Confirm new password
            <input
              className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-primary outline-none focus:border-accent"
              type="password"
              minLength={8}
              maxLength={128}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
        ) : null}

        {notice ? (
          <div className="rounded-lg border border-accent/30 bg-accent/10 p-3 text-sm text-primary">
            {notice}
          </div>
        ) : null}

        {error ? (
          <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error">
            {error}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={working}
          className="h-11 w-full rounded-lg bg-accent px-4 text-sm font-semibold text-accent-fg disabled:opacity-60"
        >
          {working
            ? "Working…"
            : mode === "create"
              ? "Create account"
              : isForgot
                ? "Send reset link"
                : isReset
                  ? "Save new password"
                  : "Sign in"}
        </button>
      </form>

      {mode === "sign-in" ? (
        <button
          type="button"
          className="mt-4 w-full text-center text-sm font-medium text-secondary hover:text-primary"
          onClick={() => chooseMode("forgot")}
        >
          Forgot password?
        </button>
      ) : null}

      {isForgot || isReset ? (
        <button
          type="button"
          className="mt-4 w-full text-center text-sm font-medium text-secondary hover:text-primary"
          onClick={() => chooseMode("sign-in")}
        >
          Back to sign in
        </button>
      ) : null}
    </AuthShell>
  );
}

function LockedAccountDelete() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [working, setWorking] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canDelete =
    password.length >= 8 && confirmation.trim() === "DELETE" && !working;

  async function submitDelete(event: FormEvent) {
    event.preventDefault();
    if (!canDelete) return;

    if (
      typeof window !== "undefined" &&
      !window.confirm(
        "Permanently delete this CatchCompass account and its personal data? This cannot be undone.",
      )
    )
      return;

    setWorking(true);
    setDeleteError(null);
    try {
      await deleteOwnAccount(password);
      if (typeof window !== "undefined") window.location.replace("/");
    } catch (error) {
      setDeleteError(
        error instanceof Error ? error.message : "Could not delete account.",
      );
      setWorking(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        className="mt-3 w-full rounded-lg px-4 py-2 text-sm font-semibold text-error hover:bg-error/10"
        onClick={() => setOpen(true)}
      >
        Delete my account
      </button>
    );
  }

  return (
    <form
      className="mt-4 space-y-3 rounded-lg border border-error/40 bg-error-weak/40 p-4"
      onSubmit={submitDelete}
    >
      <p className="text-sm font-semibold text-primary">
        Permanently delete account
      </p>
      <p className="text-xs leading-5 text-secondary">
        This removes the login and any personal CatchCompass data owned by this
        account. It cannot be undone.
      </p>
      <label className="block text-sm font-medium text-primary">
        Current password
        <input
          className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-primary outline-none focus:border-error"
          type="password"
          minLength={8}
          maxLength={128}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          required
        />
      </label>
      <label className="block text-sm font-medium text-primary">
        Type DELETE to confirm
        <input
          className="mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-primary outline-none focus:border-error"
          value={confirmation}
          onChange={(event) => setConfirmation(event.target.value)}
          autoComplete="off"
          placeholder="DELETE"
          required
        />
      </label>
      {deleteError ? (
        <p className="text-sm text-error" role="alert">
          {deleteError}
        </p>
      ) : null}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canDelete}
          className="flex-1 rounded-lg bg-error px-4 py-2.5 text-sm font-semibold text-accent-fg disabled:opacity-50"
        >
          {working ? "Deleting…" : "Delete permanently"}
        </button>
        <button
          type="button"
          className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-primary"
          onClick={() => {
            setOpen(false);
            setPassword("");
            setConfirmation("");
            setDeleteError(null);
          }}
          disabled={working}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background px-4 py-10 text-primary">
      <div className="mx-auto w-full max-w-md rounded-2xl border border-border bg-raised p-6 shadow-xl">
        <div className="mb-6 flex items-center gap-3">
          <div className="grid size-11 place-items-center rounded-full border border-accent/50 bg-inset text-lg text-accent">
            ◉
          </div>
          <div>
            <p className="font-semibold text-primary">CatchCompass</p>
            <p className="text-xs text-secondary">Secure angler access</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
